import type { BenchLevel } from '~api/services';
import { ToggleGroup, ToggleGroupItem } from '~components/ui/toggle-group';

/* -------------------------------------------------------------------------- */
/*                                    Types                                   */
/* -------------------------------------------------------------------------- */

type BenchLevelSelectProps = {
  /** Currently selected bench level */
  selected: BenchLevel;
  /** Callback when a different bench level is selected */
  onChange: (selected: BenchLevel) => void;
};

/* -------------------------------------------------------------------------- */
/*                               Main component                               */
/* -------------------------------------------------------------------------- */

/**
 * Toggle group for selecting the bench level (collar, middle, toe). Uses single selection mode.
 * Calls onChange with the new bench level when a different option is selected.
 *
 * Note: The values of the ToggleGroup.Item must match the BenchLevel type ('collar', 'middle', 'toe') for proper typing and functionality.
 *
 * @example
 * ```tsx
 * const [benchLevel, setBenchLevel] = useState<BenchLevel>('collar');
 *
 * <BenchLevelSelect selected={benchLevel} onChange={setBenchLevel} />
 * ```
 */
const BenchLevelSelect = ({ selected, onChange }: BenchLevelSelectProps) => {
  const handleValueChange = (v: string) => {
    if (v) {
      onChange(v as BenchLevel);
    }
  };

  return (
    <ToggleGroup type="single" variant="outline" size="lg" block value={selected} onValueChange={handleValueChange}>
      <ToggleGroupItem value="collar">Collar</ToggleGroupItem>
      <ToggleGroupItem value="middle">Middle</ToggleGroupItem>
      <ToggleGroupItem value="toe">Toe</ToggleGroupItem>
    </ToggleGroup>
  );
};

/* -------------------------------------------------------------------------- */
/*                                   Exports                                  */
/* -------------------------------------------------------------------------- */

export { BenchLevelSelect };
